/**
 * 付款管理（HK 網上收款）—— 由 `MePage` 抽出（UI v2 **步 9**；設計 §11 步 9：MePage ≤200）
 *
 * ⚠️ 設計原則（`decisions/028`；`rules.md` B2／B6）：
 *   · **唔准假成功**：收款未接通 → 照實顯示原因（`provider_not_chosen`／`not_configured`／`not_wired`）
 *   · **唔准填假值**：定價未定 → 顯示「未定價」＋列出**需要產品負責人提供咩**
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LegalNotice from '../../components/LegalNotice';
import { getDeviceId } from '../../hooks/useChatStream';

interface BillingSummary {
  provider?: { id?: string; configured?: boolean; reason?: string | null };
  manual?: { ready?: boolean; reason?: string | null };
  plans?: { id: string; label: string; credits: number; amountCents: number; currency: string }[];
  methods?: { id: string; label: string; qrUrl: string; note?: string }[];
  credits?: number;
  orders?: {
    id: string;
    packId: string | null;
    amountCents: number;
    currency: string;
    status: string;
    reference?: string | null;
    method?: string | null;
    createdAt: string;
  }[];
  /** ⭐ 產品負責人專用（`OWNER_EMAILS` 對得上才有） */
  owner?: {
    isOwner?: boolean;
    queue?: {
      id: string;
      packId: string | null;
      amountCents: number;
      currency: string;
      status: string;
      reference?: string | null;
      method?: string | null;
      createdAt: string;
    }[];
  };
}

/** 分 → `HK$1.50`（**唔准**自己砌價；呢個只係格式） */
function money(cents: number, currency: string): string {
  return `${currency} ${(cents / 100).toFixed(2)}`;
}

const card: React.CSSProperties = {
  background: 'var(--color-card)',
  border: '1px solid var(--color-divider)',
  borderRadius: '18px',
  padding: '14px 16px',
  marginBottom: '12px',
};
const h2: React.CSSProperties = { margin: '0 0 8px', fontSize: 'var(--font-size-label, 16px)' };
const btn: React.CSSProperties = {
  minHeight: '40px',
  padding: '0 16px',
  borderRadius: '20px',
  border: '1.5px solid var(--color-primary)',
  background: 'var(--color-primary)',
  color: 'var(--color-on-primary)',
  fontFamily: 'inherit',
  fontSize: 'var(--font-size-small, 15px)',
  fontWeight: 700,
  cursor: 'pointer',
};

export default function BillingSection() {
  const { t } = useTranslation();
  const [deviceId] = useState(() => getDeviceId());
  const [data, setData] = useState<BillingSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [packId, setPackId] = useState('');
  const [methodId, setMethodId] = useState('');
  const [order, setOrder] = useState<{ id: string; reference: string | null; status: string; method?: string | null } | null>(
    null,
  );
  const activeOrder = order;
  const activeMethod = (data?.methods ?? []).find((m) => m.id === (order?.method ?? ''));

  const load = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/billing/summary?deviceId=${encodeURIComponent(deviceId)}`);
      const json = (await res.json().catch(() => ({}))) as BillingSummary & { error?: string };
      if (!res.ok) setError(json.error ?? `http_${res.status}`);
      else setError(null);
      setData(json);
    } catch (err) {
      setError(String(err).slice(0, 80));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 建單（個人收款碼）—— ⚠️ 未接通／未定價一定失敗（API 會照實報原因） */
  const createOrder = async () => {
    setBusy(true);
    try {
      const res = await fetch('/api/billing/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId, packId, method: methodId }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        order?: { id: string; reference: string | null; status: string; method?: string | null };
        error?: string;
      };
      if (!res.ok || !json.order) setError(json.error ?? `http_${res.status}`);
      else {
        setError(null);
        setOrder(json.order);
      }
    } catch (err) {
      setError(String(err).slice(0, 80));
    } finally {
      setBusy(false);
    }
  };

  /** 撳「我已經付款」→ 等產品負責人**人手**確認（呢一步唔會加額度） */
  const claimOrder = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/billing/order/${order.id}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId }),
      });
      const json = (await res.json().catch(() => ({}))) as { status?: string; error?: string };
      if (!res.ok) setError(json.error ?? `http_${res.status}`);
      else {
        setError(null);
        setOrder({ ...order, status: json.status ?? 'claimed' });
        await load();
      }
    } catch (err) {
      setError(String(err).slice(0, 80));
    } finally {
      setBusy(false);
    }
  };

  /** ⭐ **產品負責人確認收款**（登入即可，唔需要 token；後端亦會再驗一次） */
  const confirmOrder = async (orderId: string) => {
    setBusy(true);
    try {
      const res = await fetch('/api/billing/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId }),
      });
      const json = (await res.json().catch(() => ({}))) as { credits?: number; error?: string };
      if (!res.ok) setError(json.error ?? `http_${res.status}`);
      else {
        setError(null);
        await load();
      }
    } catch (err) {
      setError(String(err).slice(0, 80));
    } finally {
      setBusy(false);
    }
  };

  const providerId = data?.provider?.id ?? 'none';
  const providerReady = Boolean(data?.provider?.configured);

  return (
    <>
      {error ? (
        <p className="page__error" role="alert">
          {t('me_load_failed')}（{error}）
          <button type="button" onClick={() => void load()} style={{ marginLeft: '8px', fontFamily: 'inherit' }}>
            {t('me_refresh')}
          </button>
        </p>
      ) : null}

      {/* ── 付款管理（HK 網上收款） ── */}
      <section style={card} data-testid="me-billing">
        <h2 style={h2}>{t('me_billing_title')}</h2>

        <p style={{ margin: '0 0 6px' }}>
          {t('me_credits')}
          <strong data-testid="me-credits"> {data?.credits ?? 0}</strong>
        </p>

        {/* ⚠️ 收款接通狀態：唔接通就照實顯示，唔准扮有得買 */}
        <p className="footnote" style={{ margin: '0 0 6px' }} data-testid="me-provider-state">
          {t('me_billing_provider')}：{providerReady ? t('me_billing_ready') : t('me_billing_not_ready')}
          {providerId !== 'none' ? `（${providerId}）` : ''}
          {!providerReady && data?.provider?.reason ? ` — ${data.provider.reason}` : ''}
        </p>

        <div data-testid="me-plans">
          {(data?.plans ?? []).length === 0 ? (
            <>
              <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{t('me_billing_no_pricing')}</p>
              <p className="footnote" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {t('me_billing_needs')}
              </p>
            </>
          ) : (data?.methods ?? []).length === 0 ? (
            /** ⭐ 0010：有價但未放收款碼 → 照實講（唔准扮有得收錢） */
            <p className="footnote" style={{ margin: 0 }} data-testid="me-methods-missing">
              {t('me_methods_not_set')}
            </p>
          ) : (
            /** ⭐ 0010：**個人收款碼**流程（FPS／AlipayHK／WeChat Pay；冇 API → 人手確認） */
            <div data-testid="me-manual">
              <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{t('me_manual_pick_pack')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                {(data?.plans ?? []).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`me-pack-${p.id}`}
                    aria-pressed={packId === p.id}
                    onClick={() => setPackId(p.id)}
                    className={packId === p.id ? 'lang-btn lang-btn--active' : 'lang-btn'}
                  >
                    {t(p.id, { defaultValue: p.label })} · {money(p.amountCents, p.currency)}
                  </button>
                ))}
              </div>
              <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{t('me_manual_pick_method')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                {(data?.methods ?? []).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`me-method-${m.id}`}
                    aria-pressed={methodId === m.id}
                    onClick={() => setMethodId(m.id)}
                    className={methodId === m.id ? 'lang-btn lang-btn--active' : 'lang-btn'}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                data-testid="me-create-order"
                disabled={!packId || !methodId || busy}
                onClick={() => void createOrder()}
                style={{ ...btn, opacity: !packId || !methodId || busy ? 0.5 : 1 }}
              >
                {t('me_manual_create')}
              </button>


              {activeOrder ? (
                <div data-testid="me-order" style={{ marginTop: '10px' }}>
                  <p style={{ margin: '0 0 4px', fontWeight: 700 }}>
                    {t('me_manual_reference')}：<span data-testid="me-reference">{activeOrder.reference}</span>
                  </p>
                  {activeMethod?.qrUrl ? (
                    <img
                      src={activeMethod.qrUrl}
                      alt={activeMethod.label}
                      style={{ width: '180px', height: '180px', objectFit: 'contain', background: '#fff', borderRadius: '12px' }}
                    />
                  ) : null}
                  {activeMethod?.note ? <p className="footnote">{activeMethod.note}</p> : null}
                  {/* ⭐ 0019（`decisions/043`）：付款畫面一定要寫明**收款方**（卡商 audit 要求） */}
                  <LegalNotice variant="payee" />
                  <p className="footnote" style={{ margin: '4px 0 6px' }}>
                    {t('me_manual_pay_hint')}
                  </p>
                  <p style={{ margin: '0 0 6px' }} data-testid="me-order-status">
                    {activeOrder.status === 'paid'
                      ? t('me_manual_paid')
                      : activeOrder.status === 'claimed'
                        ? t('me_manual_claimed')
                        : t('me_manual_pending')}
                  </p>
                  {activeOrder.status === 'pending' ? (
                    <button type="button" data-testid="me-claim" disabled={busy} onClick={() => void claimOrder()} style={btn}>
                      {t('me_manual_claim_button')}
                    </button>
                  ) : null}
                  <p className="footnote">{t('me_manual_confirm_note')}</p>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* ⭐ 2026-09-26：**產品負責人專用**（確認收款；`OWNER_EMAILS` 對得上才有） */}
        {data?.owner?.isOwner ? (
          <div data-testid="me-owner-queue" style={{ marginTop: '14px' }}>
            <h2 style={{ ...h2, marginTop: '6px' }}>{t('me_owner_queue_title')}</h2>
            {(data.owner.queue ?? []).length === 0 ? (
              <p className="footnote" style={{ margin: 0 }} data-testid="me-owner-queue-empty">
                {t('me_owner_queue_empty')}
              </p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {(data.owner.queue ?? []).map((o) => (
                  <li key={o.id} style={{ marginBottom: '6px' }}>
                    <span data-testid={`me-owner-order-${o.id}`}>
                      {o.method ?? '?'} · {money(o.amountCents, o.currency)} · {o.reference ?? '—'} · {o.status}
                    </span>{' '}
                    <button
                      type="button"
                      data-testid={`me-owner-confirm-${o.id}`}
                      disabled={busy || o.status === 'paid'}
                      onClick={() => void confirmOrder(o.id)}
                      style={{ ...btn, minHeight: '30px', marginTop: '4px' }}
                    >
                      {o.status === 'paid' ? t('me_owner_confirmed') : t('me_owner_confirm_button')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="footnote" style={{ margin: '6px 0 0' }}>
              {t('me_owner_queue_hint')}
            </p>
          </div>
        ) : null}

        {/* 訂單（照實列；冇就講冇） */}
        <h2 style={{ ...h2, marginTop: '14px' }}>{t('me_orders_title')}</h2>
        {(data?.orders ?? []).length === 0 ? (
          <p className="footnote" style={{ margin: 0 }} data-testid="me-orders-empty">
            {t('me_orders_empty')}
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '18px' }}>
            {(data?.orders ?? []).map((o) => (
              <li key={o.id}>
                {money(o.amountCents, o.currency)} · {o.status} · {new Date(o.createdAt).toLocaleDateString()}
              </li>
            ))}
          </ul>
        )}

        <p className="footnote" style={{ margin: '10px 0 0' }}>
          {busy ? t('chat_thinking') : t('me_billing_refund_note')}
        </p>
      </section>
    </>
  );
}

